// CRM service — Firestore CRUD for crm_contacts, activities, and tasks.
//
// Phase 1 surface: contacts CRUD + search. Activities/tasks functions are
// stubbed here so Phase 2/3 can fill them in without rewiring imports.
//
// All getDocs() calls include limit(). Search is prefix-based on emailLower
// and displayName (Firestore-native; swap to Typesense behind this module
// later if we ever outgrow ~50k contacts).

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDoc,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../config/firebase';

const CONTACTS = 'crm_contacts';
const TASKS = 'crm_tasks';

// In-memory stats cache (mirrors the pattern in services/analytics.js).
const STATS_CACHE_TTL_MS = 5 * 60 * 1000;
let statsCache = { at: 0, value: null };

// Team-member list cache — used by the assignee / internal-POC dropdowns.
// Membership rarely changes; 5 minutes is more than enough freshness.
const TEAM_CACHE_TTL_MS = 5 * 60 * 1000;
let teamCache = { at: 0, value: null };

export function clearCrmCache() {
  statsCache = { at: 0, value: null };
  teamCache = { at: 0, value: null };
}

// ----- helpers --------------------------------------------------------------

const buildDisplayName = (firstName, lastName) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const norm = (s) => (typeof s === 'string' ? s.trim() : '');
const normLower = (s) => norm(s).toLowerCase();
const digitsOnly = (s) => norm(s).replace(/\D+/g, '');

const cleanPatch = (obj) => {
  // Strip undefined fields so Firestore doesn't reject the write.
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
};

// ----- contacts -------------------------------------------------------------

/**
 * List contacts with optional filters and cursor pagination.
 *
 * @param {object} opts
 * @param {string} [opts.status]         — exact match
 * @param {string} [opts.pipelineStage]  — exact match
 * @param {string} [opts.type]           — exact match
 * @param {DocumentSnapshot} [opts.cursor] — pass the last doc snapshot from
 *                                            the previous page to get the next.
 * @param {number} [opts.pageSize=50]
 * @param {boolean} [opts.includeArchived=false]
 * @returns {Promise<{ contacts: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function listContacts({
  status,
  pipelineStage,
  type,
  cursor = null,
  pageSize = 50,
  includeArchived = false,
} = {}) {
  const constraints = [];
  if (!includeArchived) constraints.push(where('archivedAt', '==', null));
  if (status) constraints.push(where('status', '==', status));
  if (pipelineStage) constraints.push(where('pipelineStage', '==', pipelineStage));
  if (type) constraints.push(where('type', '==', type));
  constraints.push(orderBy('updatedAt', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize));

  const snap = await getDocs(query(collection(db, CONTACTS), ...constraints));
  const contacts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return {
    contacts,
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    hasMore: snap.docs.length === pageSize,
  };
}

export async function getContact(id) {
  const ref = doc(db, CONTACTS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Case-insensitive prefix search across name, email, company (venue), and
 * phone digits. Runs the queries in parallel and de-dupes by doc id.
 *
 * Firestore "starts-with" is implemented as a range query (>= q, <= q + high
 * sentinel). Each field is searched independently because Firestore can't OR
 * across fields in a single query. Existing contacts that pre-date this
 * change won't have the *Lower / phoneDigits fields and won't surface until
 * the one-time backfillCrmContactSearchIndex Cloud Function has run.
 */
export async function searchContacts(rawQueryStr) {
  const q = norm(rawQueryStr);
  if (!q) return [];
  const qLower = q.toLowerCase();
  // High Unicode code point used as the upper-bound sentinel for prefix
  // range queries — sits above all standard text so [qLower, upper] matches
  // any string that starts with qLower.
  const HIGH = '';
  const upper = qLower + HIGH;
  const qDigits = digitsOnly(q);

  const prefixQuery = (field, low, high) => query(
    collection(db, CONTACTS),
    where(field, '>=', low),
    where(field, '<=', high),
    limit(25),
  );

  const queries = [
    ['emailLower',       prefixQuery('emailLower', qLower, upper)],
    ['displayNameLower', prefixQuery('displayNameLower', qLower, upper)],
    ['companyLower',     prefixQuery('companyLower', qLower, upper)],
  ];
  if (qDigits.length >= 3) {
    queries.push(['phoneDigits', prefixQuery('phoneDigits', qDigits, qDigits + HIGH)]);
  }

  const snaps = await Promise.all(queries.map(([name, qq]) =>
    getDocs(qq).catch((e) => {
      console.warn(`[crm.search] ${name} query failed:`, e);
      return { docs: [] };
    }),
  ));

  const byId = new Map();
  for (const snap of snaps) {
    for (const d of snap.docs) {
      if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
    }
  }
  return Array.from(byId.values());
}

/**
 * Create a new contact. Returns the new doc id.
 * Required: at least one of email / phone / (firstName + lastName).
 */
export async function createContact(data) {
  const firstName = norm(data.firstName);
  const lastName = norm(data.lastName);
  const email = norm(data.email);
  const phone = norm(data.phone);
  const displayName = buildDisplayName(firstName, lastName) || email || phone || 'Untitled contact';

  const company = norm(data.company);
  const payload = cleanPatch({
    firstName,
    lastName,
    displayName,
    displayNameLower: normLower(displayName),
    email,
    emailLower: normLower(email),
    phone,
    phoneDigits: digitsOnly(phone),
    company,
    companyLower: normLower(company),
    title: norm(data.title),
    addressLine1: norm(data.addressLine1),
    addressLine2: norm(data.addressLine2),
    city: norm(data.city),
    state: norm(data.state),
    postalCode: norm(data.postalCode),
    type: data.type || 'lead',
    status: data.status || 'new',
    pipelineStage: data.pipelineStage || 'new',
    temperature: data.temperature || 'cold',
    // When the user explicitly picks a temperature in the form, this is set
    // to true and the nightly Cloud Function will skip the auto-derived
    // value. Switching back to "Auto" sets this to false.
    temperatureOverride: data.temperatureOverride === true,
    leadScore: typeof data.leadScore === 'number' ? data.leadScore : 0,
    tags: Array.isArray(data.tags) ? data.tags : [],
    ownerName: norm(data.ownerName),
    notes: norm(data.notes),
    source: data.source || 'manual',
    linkedInquiryId: data.linkedInquiryId || null,
    referredByContactId: data.referredByContactId || null,
    referredByContactName: norm(data.referredByContactName),
    archivedAt: null,
    createdBy: auth.currentUser?.uid || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const ref = await addDoc(collection(db, CONTACTS), payload);
  clearCrmCache();
  return ref.id;
}

/**
 * Update fields on a contact. Patch is shallow-merged. Recomputes
 * displayName + emailLower if the source fields change.
 */
export async function updateContact(id, patch) {
  const out = cleanPatch({ ...patch });

  if ('firstName' in out || 'lastName' in out) {
    const current = await getContact(id);
    const firstName = 'firstName' in out ? norm(out.firstName) : current?.firstName || '';
    const lastName = 'lastName' in out ? norm(out.lastName) : current?.lastName || '';
    out.firstName = firstName;
    out.lastName = lastName;
    out.displayName = buildDisplayName(firstName, lastName) || current?.email || current?.phone || 'Untitled contact';
    out.displayNameLower = normLower(out.displayName);
  }
  if ('email' in out) {
    out.email = norm(out.email);
    out.emailLower = normLower(out.email);
  }
  if ('phone' in out) {
    out.phone = norm(out.phone);
    out.phoneDigits = digitsOnly(out.phone);
  }
  if ('company' in out) {
    out.company = norm(out.company);
    out.companyLower = normLower(out.company);
  }
  out.updatedAt = serverTimestamp();

  await updateDoc(doc(db, CONTACTS, id), out);
  clearCrmCache();
}

/**
 * Soft-delete a contact. Sets archivedAt; data stays in Firestore.
 */
export async function archiveContact(id) {
  await updateDoc(doc(db, CONTACTS, id), {
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  clearCrmCache();
}

/**
 * Restore an archived contact.
 */
export async function unarchiveContact(id) {
  await updateDoc(doc(db, CONTACTS, id), {
    archivedAt: null,
    updatedAt: serverTimestamp(),
  });
  clearCrmCache();
}

/**
 * Bulk-update a set of contacts. `patch` is the same shape as updateContact's
 * patch (any combination of pipelineStage, ownerName, etc.). To add a tag,
 * pass `addTag: 'string'` — implemented via arrayUnion so tags don't dupe.
 * To archive, pass `archive: true`.
 *
 * Uses Firestore batched writes — max 500 ops per batch.
 */
export async function bulkUpdateContacts(ids, patch) {
  if (!Array.isArray(ids) || ids.length === 0) return;
  const { addTag, archive, ...rest } = patch || {};

  const update = { ...rest, updatedAt: serverTimestamp() };
  if (archive) update.archivedAt = serverTimestamp();
  if (addTag) update.tags = arrayUnion(addTag);

  const CHUNK = 400;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const id of ids.slice(i, i + CHUNK)) {
      batch.update(doc(db, CONTACTS, id), update);
    }
    await batch.commit();
  }
  clearCrmCache();
}

// ----- activities (Phase 2 surface; safe stubs for Phase 1) -----------------

export async function listActivities(contactId, { pageSize = 50 } = {}) {
  const snap = await getDocs(query(
    collection(db, CONTACTS, contactId, 'activities'),
    orderBy('occurredAt', 'desc'),
    limit(pageSize),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addActivity(contactId, activity) {
  const payload = cleanPatch({
    type: activity.type || 'note',
    subject: norm(activity.subject),
    body: norm(activity.body),
    occurredAt: activity.occurredAt || serverTimestamp(),
    source: activity.source || 'manual',
    sourceRef: activity.sourceRef || null,
    authorName: norm(activity.authorName) || auth.currentUser?.email || 'system',
    createdAt: serverTimestamp(),
  });
  const ref = await addDoc(collection(db, CONTACTS, contactId, 'activities'), payload);
  // Bump the contact's lastInteractionAt so list views can sort by it.
  await updateDoc(doc(db, CONTACTS, contactId), {
    lastInteractionAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

// ----- tasks (Phase 3 surface; safe stubs for Phase 1) ----------------------

export async function listTasks({ contactId, status, assigneeName, pageSize = 100 } = {}) {
  const constraints = [];
  if (contactId) constraints.push(where('contactId', '==', contactId));
  if (status) constraints.push(where('status', '==', status));
  if (assigneeName) constraints.push(where('assigneeName', '==', assigneeName));
  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(pageSize));
  const snap = await getDocs(query(collection(db, TASKS), ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createTask(data) {
  const payload = cleanPatch({
    contactId: data.contactId || null,
    contactName: norm(data.contactName) || null,
    inquiryId: data.inquiryId || null,
    title: norm(data.title),
    description: norm(data.description),
    status: data.status || 'todo',
    priority: data.priority || 'medium',
    type: data.type || 'general',
    dueAt: data.dueAt || null,
    assigneeName: norm(data.assigneeName),
    createdAt: serverTimestamp(),
    completedAt: null,
  });
  const ref = await addDoc(collection(db, TASKS), payload);
  clearCrmCache();
  return ref.id;
}

export async function updateTask(id, patch) {
  const out = cleanPatch({ ...patch });
  if (out.status === 'done' && !('completedAt' in out)) {
    out.completedAt = serverTimestamp();
  }
  await updateDoc(doc(db, TASKS, id), out);
  clearCrmCache();
}

export async function deleteTask(id) {
  await deleteDoc(doc(db, TASKS, id));
  clearCrmCache();
}

// ----- dashboard stats ------------------------------------------------------

const PIPELINE_STAGE_IDS = ['new', 'contacted', 'qualified', 'demo', 'proposal', 'negotiation', 'won', 'lost'];
const OPEN_STAGE_IDS = ['contacted', 'qualified', 'demo', 'proposal', 'negotiation'];

/**
 * What counts as a hot lead — 75, matching leadScoring.js's deriveTemperature
 * (score >= 75 => 'hot') and the aiDailyPriorities query.
 *
 * This KPI used 80. The same contact therefore wore a "hot" badge on its own
 * row, fed the AI priorities panel as hot, and was absent from the dashboard's
 * Hot Leads count — three surfaces, two definitions, on a scale where recency
 * decays exponentially so scores cluster well below the ceiling. One
 * threshold, exported so a future surface has nowhere else to get it.
 */
export const HOT_LEAD_SCORE = 75;

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * A count that failed is NOT a count of zero.
 *
 * Returning 0 on error meant a missing composite index or a rules change
 * rendered "Hot Leads 0" on the dashboard while the Contacts page listed the
 * contacts perfectly well — a number that is wrong and confident. null now
 * propagates, and the KPI renders an error state instead of a figure.
 */
const countSafe = async (q) => {
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch (err) {
    console.warn('[crm.stats] count query failed:', err);
    return null;
  }
};

/**
 * High-level CRM stats for the dashboard. Caches in-memory for 5 minutes;
 * pass { force: true } to bypass the cache after a mutation.
 *
 * Returns:
 *   {
 *     totalContacts, hotLeads, openPipeline, tasksDueToday,
 *     byStage: { nurture, outreach, engaged, proposal, negotiation, won, lost },
 *     overdueTasks
 *   }
 */
export async function getCrmStats({ force = false } = {}) {
  if (!force && statsCache.value && Date.now() - statsCache.at < STATS_CACHE_TTL_MS) {
    return statsCache.value;
  }

  const contactsCol = collection(db, CONTACTS);
  const tasksCol = collection(db, TASKS);
  const activeContacts = query(contactsCol, where('archivedAt', '==', null));

  const stagePromises = PIPELINE_STAGE_IDS.map((stage) =>
    countSafe(query(contactsCol, where('archivedAt', '==', null), where('pipelineStage', '==', stage))),
  );

  const [
    totalContacts,
    hotLeads,
    stageCounts,
    tasksDueToday,
    overdueTasks,
  ] = await Promise.all([
    countSafe(activeContacts),
    countSafe(query(contactsCol, where('archivedAt', '==', null), where('leadScore', '>=', HOT_LEAD_SCORE))),
    Promise.all(stagePromises),
    countSafe(query(
      tasksCol,
      where('status', 'in', ['todo', 'in_progress']),
      where('dueAt', '>=', Timestamp.fromDate(startOfToday())),
      where('dueAt', '<=', Timestamp.fromDate(endOfToday())),
    )),
    countSafe(query(
      tasksCol,
      where('status', 'in', ['todo', 'in_progress']),
      where('dueAt', '<', Timestamp.fromDate(startOfToday())),
    )),
  ]);

  const byStage = {};
  PIPELINE_STAGE_IDS.forEach((stage, i) => { byStage[stage] = stageCounts[i]; });
  // A failed stage count must not be summed in as 0 — the funnel would read
  // as a real, smaller pipeline. The open-pipeline total is null if any of
  // its stages could not be counted.
  const openStagesFailed = OPEN_STAGE_IDS.some((stage) => byStage[stage] === null);
  const openPipeline = openStagesFailed
    ? null
    : OPEN_STAGE_IDS.reduce((sum, s) => sum + (byStage[s] || 0), 0);

  const value = { totalContacts, hotLeads, openPipeline, tasksDueToday, byStage, overdueTasks };
  // Never cache a degraded answer for 5 minutes — a transient index or rules
  // failure would otherwise outlive itself on every dashboard in the org.
  const degraded = Object.values(value).some((v) => v === null)
    || PIPELINE_STAGE_IDS.some((stage) => byStage[stage] === null);
  if (!degraded) statsCache = { at: Date.now(), value };
  return value;
}

/**
 * Last N contacts created (newest first). Used by dashboard "Recently added"
 * panel until Phase 2 ships the unified activity timeline.
 */
export async function listRecentContacts({ pageSize = 8 } = {}) {
  const snap = await getDocs(query(
    collection(db, CONTACTS),
    where('archivedAt', '==', null),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Contacts that have not been touched in `staleDays` days. Used by the
 * "Stalled" panel on the dashboard.
 */
export async function listStalledContacts({ staleDays = 30, pageSize = 8 } = {}) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - staleDays);
  const snap = await getDocs(query(
    collection(db, CONTACTS),
    where('archivedAt', '==', null),
    where('updatedAt', '<', Timestamp.fromDate(cutoff)),
    orderBy('updatedAt', 'asc'),
    limit(pageSize),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ----- lead scoring ---------------------------------------------------------

/**
 * List contacts ordered by leadScore desc — used by the Lead Scoring page.
 * Cursor-paginated; returns the same shape as listContacts.
 */
export async function listContactsByScore({ cursor = null, pageSize = 50, minScore = null, temperature = null } = {}) {
  const constraints = [where('archivedAt', '==', null)];
  if (typeof minScore === 'number') constraints.push(where('leadScore', '>=', minScore));
  if (temperature) constraints.push(where('temperature', '==', temperature));
  constraints.push(orderBy('leadScore', 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize));
  const snap = await getDocs(query(collection(db, CONTACTS), ...constraints));
  return {
    contacts: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    hasMore: snap.docs.length === pageSize,
  };
}

/**
 * Trigger a single-contact rescore via the Cloud Function callable. Returns
 * { leadScore, temperature, activitiesLast90d } from the server so the UI
 * can reflect the fresh score immediately.
 */
export async function recomputeLeadScore(contactId) {
  const fn = httpsCallable(functions, 'recomputeLeadScoreOnDemand');
  const res = await fn({ contactId });
  clearCrmCache();
  return res.data;
}

// ----- campaigns ------------------------------------------------------------

const CAMPAIGNS = 'crm_campaigns';

export async function listCampaigns({ pageSize = 50 } = {}) {
  const snap = await getDocs(query(
    collection(db, CAMPAIGNS),
    orderBy('updatedAt', 'desc'),
    limit(pageSize),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getCampaign(id) {
  const ref = doc(db, CAMPAIGNS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function createCampaign(data) {
  const payload = cleanPatch({
    name: norm(data.name) || 'Untitled campaign',
    audience: data.audience || null,
    subject: norm(data.subject),
    body: norm(data.body),
    tone: data.tone || 'friendly',
    status: 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: auth.currentUser?.uid || null,
  });
  const ref = await addDoc(collection(db, CAMPAIGNS), payload);
  return ref.id;
}

export async function updateCampaign(id, patch) {
  const out = cleanPatch({ ...patch, updatedAt: serverTimestamp() });
  await updateDoc(doc(db, CAMPAIGNS, id), out);
}

export async function deleteCampaign(id) {
  await deleteDoc(doc(db, CAMPAIGNS, id));
}

/**
 * Estimate audience size + grab a sample for the campaign builder preview.
 *
 * Filters supported:
 *   - orgId  : require user.customerOfOrgs.includes(orgId)
 *   - boothId: require user.customerOfBooths.includes(boothId)
 *   - minSessions: filter by customerStats[orgId]?.sessionCount or boothStats
 *
 * Uses array-contains on customerOfOrgs / customerOfBooths. Caps at 1000 to
 * keep this snappy for the demo; an actual sender would page through.
 */
export async function previewAudience({ orgId, boothId, minSessions = 0 } = {}) {
  if (!orgId && !boothId) {
    return { count: 0, sample: [], totalScanned: 0 };
  }
  const constraints = [];
  if (orgId) constraints.push(where('customerOfOrgs', 'array-contains', orgId));
  else if (boothId) constraints.push(where('customerOfBooths', 'array-contains', boothId));
  constraints.push(limit(1000));

  const snap = await getDocs(query(collection(db, 'users'), ...constraints));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Filter for min sessions client-side. Cheap enough at ≤1000.
  const filtered = minSessions > 0
    ? all.filter((u) => {
        const stats = orgId
          ? u.customerStats?.[orgId]
          : (boothId ? u.boothStats?.[boothId] : null);
        return (stats?.sessionCount || 0) >= minSessions;
      })
    : all;

  // Only customers with an email can actually be sent to.
  const sendable = filtered.filter((u) => u.email);

  const sample = sendable.slice(0, 6).map((u) => ({
    id: u.id,
    displayName: u.displayName || '',
    email: u.email || '',
    firstName: (u.displayName || u.email || '').split(/[\s@]/)[0] || '',
  }));

  return {
    count: sendable.length,
    totalScanned: all.length,
    sample,
    capped: all.length === 1000,
  };
}

export async function aiGenerateCampaignEmail({ audienceDescription, goal = '', tone = 'friendly' } = {}) {
  const fn = httpsCallable(functions, 'aiGenerateCampaignEmail');
  const res = await fn({ audienceDescription, goal, tone });
  return res.data;
}

/**
 * Helpers to populate audience-picker dropdowns. Both are bounded queries —
 * we only need names + ids for selection.
 */
export async function listOrganizationsForCampaigns() {
  const snap = await getDocs(query(collection(db, 'organizations'), limit(200)));
  return snap.docs
    .map((d) => ({ id: d.id, name: d.data().name || d.id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function listBoothsForCampaigns({ orgId = null } = {}) {
  const constraints = [];
  if (orgId) constraints.push(where('organizationId', '==', orgId));
  constraints.push(limit(200));
  const snap = await getDocs(query(collection(db, 'booths'), ...constraints));
  return snap.docs
    .map((d) => ({ id: d.id, name: d.data().name || d.id, organizationId: d.data().organizationId }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ----- automation rules -----------------------------------------------------

const AUTOMATION_RULES = 'crm_automation_rules';

export async function listAutomationRules() {
  const snap = await getDocs(query(
    collection(db, AUTOMATION_RULES),
    orderBy('createdAt', 'desc'),
    limit(100),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createAutomationRule(data) {
  const payload = cleanPatch({
    name: norm(data.name) || 'Untitled rule',
    enabled: data.enabled !== false,
    trigger: data.trigger,
    stage: data.stage || null,
    staleDays: typeof data.staleDays === 'number' ? data.staleDays : null,
    taskTitle: norm(data.taskTitle) || 'Follow up',
    taskPriority: data.taskPriority || 'medium',
    taskType: data.taskType || 'follow_up',
    taskDueOffsetHours: typeof data.taskDueOffsetHours === 'number' ? data.taskDueOffsetHours : 24,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastRunAt: null,
    lastTaskCount: 0,
  });
  const ref = await addDoc(collection(db, AUTOMATION_RULES), payload);
  return ref.id;
}

export async function updateAutomationRule(id, patch) {
  const out = cleanPatch({ ...patch, updatedAt: serverTimestamp() });
  await updateDoc(doc(db, AUTOMATION_RULES, id), out);
}

export async function deleteAutomationRule(id) {
  await deleteDoc(doc(db, AUTOMATION_RULES, id));
}

// ----- referral network -----------------------------------------------------

/**
 * List all non-archived contacts of type 'referral_partner'. For each, also
 * fetch the count of contacts that have referredByContactId == this partner's id.
 * Returns [{ ...partner, referralCount }] sorted by referralCount desc.
 */
export async function listReferralPartners({ pageSize = 100 } = {}) {
  const partnersSnap = await getDocs(query(
    collection(db, CONTACTS),
    where('archivedAt', '==', null),
    where('type', '==', 'referral_partner'),
    limit(pageSize),
  ));
  const partners = partnersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // For each partner, count referrals via a count query. Cheap and bounded.
  const counts = await Promise.all(partners.map(async (p) => {
    try {
      const snap = await getCountFromServer(query(
        collection(db, CONTACTS),
        where('archivedAt', '==', null),
        where('referredByContactId', '==', p.id),
      ));
      return snap.data().count;
    } catch (_) {
      return 0;
    }
  }));

  const enriched = partners.map((p, i) => ({ ...p, referralCount: counts[i] }));
  enriched.sort((a, b) => (b.referralCount - a.referralCount) || (b.leadScore || 0) - (a.leadScore || 0));
  return enriched;
}

/**
 * List contacts referred by a given partner (newest first). Used to drill
 * into a referral partner's pipeline.
 */
export async function listContactsReferredBy(partnerId, { pageSize = 50 } = {}) {
  const snap = await getDocs(query(
    collection(db, CONTACTS),
    where('archivedAt', '==', null),
    where('referredByContactId', '==', partnerId),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ----- AI assistant ---------------------------------------------------------

/**
 * Ask Claude to brief a sales caller on a contact (4 sentences).
 * Returns { brief, model }.
 */
export async function aiBriefContact(contactId) {
  const fn = httpsCallable(functions, 'aiBriefContact');
  const res = await fn({ contactId });
  return res.data;
}

/**
 * Generate a tailored email or SMS draft to send to a contact.
 * Returns { draft, channel, model }.
 */
export async function aiDraftFollowup(contactId, { channel = 'email', goal = '' } = {}) {
  const fn = httpsCallable(functions, 'aiDraftFollowup');
  const res = await fn({ contactId, channel, goal });
  return res.data;
}

/**
 * Get 3-5 high-leverage actions to take today, ranked by impact.
 * Returns { priorities: [{ title, reason, kind: 'contact'|'task', refId }], generatedAt, model }.
 */
export async function aiDailyPriorities() {
  const fn = httpsCallable(functions, 'aiDailyPriorities');
  const res = await fn({});
  return res.data;
}

// ----- team members ---------------------------------------------------------

/**
 * List internal team members — used by the assignee + internal-POC dropdowns.
 * Pulls from users where role is admin or superadmin (the people who can log
 * into this admin app and actually do CRM work). Cached for 5 minutes; the
 * cache is invalidated on every mutation via clearCrmCache() — overkill for
 * this list specifically, but it keeps state hygiene simple.
 *
 * Returns: [{ uid, name, email, role }] sorted by name asc.
 */
export async function listTeamMembers() {
  if (teamCache.value && Date.now() - teamCache.at < TEAM_CACHE_TTL_MS) {
    return teamCache.value;
  }
  try {
    const snap = await getDocs(query(
      collection(db, 'users'),
      where('role', 'in', ['admin', 'superadmin']),
      limit(200),
    ));
    const rows = snap.docs.map((d) => {
      const data = d.data();
      const name = (data.displayName || '').trim() || (data.email || '').trim() || d.id;
      return { uid: d.id, name, email: data.email || '', role: data.role || '' };
    }).sort((a, b) => a.name.localeCompare(b.name));
    teamCache = { at: Date.now(), value: rows };
    return rows;
  } catch (err) {
    console.warn('[crm.listTeamMembers] failed:', err);
    return [];
  }
}

// ----- task constants -------------------------------------------------------

export const TASK_STATUSES = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'done', label: 'Done' },
];

export const TASK_PRIORITIES = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'urgent', label: 'Urgent' },
];

export const TASK_TYPES = [
  { id: 'general', label: 'General' },
  { id: 'call', label: 'Call' },
  { id: 'email', label: 'Email' },
  { id: 'meeting', label: 'Meeting' },
  { id: 'follow_up', label: 'Follow-up' },
];

// ----- constants for UI -----------------------------------------------------

export const CONTACT_TYPES = [
  { id: 'lead', label: 'Lead' },
  { id: 'venue_prospect', label: 'Venue Prospect' },
  { id: 'venue_contact', label: 'Venue Contact' },
  { id: 'referral_partner', label: 'Referral Partner' },
  { id: 'other', label: 'Other' },
];

export const CONTACT_STATUSES = [
  { id: 'new', label: 'New' },
  { id: 'active', label: 'Active' },
  { id: 'nurturing', label: 'Nurturing' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

// Standard B2B outbound funnel. Ordered left-to-right for the kanban.
// `new` is the entry point (manual add or booth-inquiry trigger); `won` and
// `lost` are terminal. Old stages (nurture/outreach/engaged) are no longer
// generated — any legacy doc still carrying one will fall through and get
// surfaced under `new` in the kanban so it can be re-staged manually.
export const PIPELINE_STAGES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'demo', label: 'Demo' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'negotiation', label: 'Negotiation' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];

export const TEMPERATURES = [
  { id: 'hot', label: 'Hot' },
  { id: 'warm', label: 'Warm' },
  { id: 'cold', label: 'Cold' },
];
